export type PopulationAllocationResult = {
  populations: number[];
  totalAllocated: number;
  normalized: boolean;
  warnings: string[];
};

const SHARE_SUM_TOLERANCE = 0.5;

export function allocatePopulationsByShare(
  totalUsers: number,
  sharePercents: Array<number | null | undefined>,
): PopulationAllocationResult {
  const warnings: string[] = [];
  if (!Array.isArray(sharePercents) || sharePercents.length === 0) {
    return { populations: [], totalAllocated: 0, normalized: false, warnings };
  }

  const total = Number.isFinite(totalUsers) ? Math.max(0, Math.round(totalUsers)) : 0;
  if (total === 0) {
    warnings.push('baseConcurrentUsers is 0; population allocation yields all zeros.');
    return {
      populations: sharePercents.map(() => 0),
      totalAllocated: 0,
      normalized: false,
      warnings,
    };
  }

  const shares = sharePercents.map((v) => (Number.isFinite(v as number) ? Math.max(0, v as number) : 0));
  const sumShares = shares.reduce((sum, v) => sum + v, 0);

  let normalized = false;
  let weights: number[];

  if (sumShares <= 0) {
    normalized = true;
    warnings.push('sharePct sums to 0; using equal split for population allocation.');
    const equal = 1 / shares.length;
    weights = shares.map(() => equal);
  } else {
    if (Math.abs(sumShares - 100) > SHARE_SUM_TOLERANCE) {
      normalized = true;
      warnings.push(`sharePct sums to ${sumShares.toFixed(1)}; normalizing to 100 for allocation.`);
    }
    weights = shares.map((v) => v / sumShares);
  }

  const raw = weights.map((w) => w * total);
  const floored = raw.map((v) => Math.floor(v));
  let remainder = total - floored.reduce((sum, v) => sum + v, 0);

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; k < order.length && remainder > 0; k += 1) {
    floored[order[k].i] += 1;
    remainder -= 1;
  }

  return {
    populations: floored,
    totalAllocated: floored.reduce((sum, v) => sum + v, 0),
    normalized,
    warnings,
  };
}
